/**
 * One litter: the facts about it, and the board of puppies in it.
 *
 * The board is the point of the screen. Everything above it — the dates, the
 * parents, the status — is one block of small type that a breeder reads once and
 * then never again, so it is drawn as one block of small type and not as four
 * cards competing with the photographs underneath.
 *
 * **Editing is a mode, not a control per field.** The status used to sit on this
 * page as three live buttons, which meant the one property of a litter anybody
 * could change was the one nobody came here to change — and it changed on the
 * first stray tap, with no way back. Now the whole record is edited through one
 * Edit button, in the same form the litter was created with, and nothing is
 * written until Save.
 *
 * A puppy is a *page* — tapping a tile navigates. Adding one is still done from
 * here, in a panel under the board, because that is a thing done to the litter
 * and usually several times in a row.
 */
import {
  canSeePlannedLitters,
  canView,
  canWriteHealth,
  canWriteLitters,
  type Offspring,
} from '@puppy-doctor/platform';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil, Plus, Scale } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnimals } from '@/api/animals.ts';
import { useLitter } from '@/api/litters.ts';
import { useMeasurementEntries } from '@/api/measurements.ts';
import { usePhotoSessions } from '@/api/photoSessions.ts';
import { createOffspring, updateLitter } from '@/api/mutations.ts';
import { inLitter, useOffspring } from '@/api/offspring.ts';
import { useUser } from '@/auth/AuthProvider.tsx';
import { Breadcrumbs } from '@/components/Breadcrumbs.tsx';
import { PhotoLog } from '@/components/gallery/PhotoLog.tsx';
import { PhotoTimeline } from '@/components/gallery/PhotoTimeline.tsx';
import { GrowthSection } from '@/components/growth/GrowthSection.tsx';
import { MeasurementLog } from '@/components/growth/MeasurementLog.tsx';
import { LitterFacts } from '@/components/LitterFacts.tsx';
import {
  type LitterDraft,
  LitterForm,
  litterDraftOf,
} from '@/components/LitterForm.tsx';
import { LitterStatusBadge } from '@/components/LitterCard.tsx';
import { OffspringGrid } from '@/components/OffspringGrid.tsx';
import { TaskSection } from '@/components/tasks/TaskSection.tsx';
import { PHOTOS_PARAM, PhotoWizard, SESSION_PARAM } from '@/components/PhotoWizard.tsx';
import {
  birthWeightOf,
  type Draft,
  DraftCard,
  draftAt,
  isComplete,
} from '@/components/OffspringDraft.tsx';
import { Button } from '@/components/ui.tsx';
import { ENTRY_PARAM, WEIGH_PARAM, WeighWizard } from '@/components/WeighWizard.tsx';
import { type Search, useUrlPanel, withoutParam } from '@/lib/useUrlPanel.ts';
import { SHARE_ID_PARAM } from '@/offline/sharedPhotos.ts';
import { useSharedPhotos } from '@/offline/useSharedPhotos.ts';
import { useAccess, useTenantId } from '@/tenant/TenantProvider.tsx';

const EDIT_PARAM = 'edit';

export const Route = createFileRoute('/litters/$litterId')({
  component: LitterPage,
});

function LitterPage() {
  const { t } = useTranslation();
  const { litterId } = Route.useParams();
  const tenantId = useTenantId();
  const { role, scopes } = useAccess();
  const navigate = useNavigate();

  /**
   * WHAT THIS READER IS HERE FOR, decided before anything is subscribed to.
   *
   * A viewer is invited to watch one part of a litter's record — the weighings,
   * the photographs, or both — and the security rules refuse them the other
   * collection outright. So these are not flags that hide a section after it has
   * loaded: they are what decides whether the listener is opened at all, because
   * a listener the rules refuse fails terminally and would take the page down
   * with it. See `canView` in platform/src/roles.ts and api/useLiveQuery.ts.
   *
   * Everybody who is in the kennel to run it passes both.
   */
  const mayReadWeights = role !== null && canView(role, scopes, 'measurements');
  const mayReadPhotos = role !== null && canView(role, scopes, 'photos');
  const mayReadTasks = role !== null && canView(role, scopes, 'tasks');

  const { data: litter, isPending } = useLitter(tenantId, litterId);
  const { data: all } = useOffspring(tenantId);
  const { data: animals } = useAnimals(tenantId);

  /**
   * The litter's own editor, seeded at the moment it opens.
   *
   * The draft is state rather than derived, because the parents are typed as
   * names and the document holds ids: seeding at open means `animals` is a list
   * the reader has already been shown, where seeding on mount could catch it
   * still loading and put two empty parent fields in front of them.
   */
  const [editing, setEditing] = useState<LitterDraft | null>(null);
  const [adding, setAdding] = useState(false);
  const { uid } = useUser();

  // So the back button closes the editor the same way Cancel does. See
  // lib/useUrlPanel.ts. `adding` goes with it for the reason `closeEditor`
  // below says.
  const editPanel = useUrlPanel(EDIT_PARAM, editing !== null, () => {
    setEditing(null);
    setAdding(false);
  });

  /**
   * Shutting the editor, from Cancel or from Save.
   *
   * One function because there are now two things to put away: the litter's own
   * form and the puppy form that opens inside it. A half-filled `OffspringDraft`
   * left behind a closed editor is state nothing renders — reopening the pencil
   * would show it again, holding a name typed minutes ago for a puppy the reader
   * had already given up on.
   */
  const closeEditor = () => {
    setEditing(null);
    setAdding(false);
    editPanel.markClosed();
  };

  /**
   * Open whenever `weigh` is in the URL, at whichever of its own steps that
   * value names — see the doc comment on `WeighWizard`, which owns everything
   * about that param past "is it there at all". This route only ever writes
   * two values for it: `'select'`, from the button below, and gone entirely,
   * from `onClose`; every other value is `WeighWizard`'s own doing.
   */
  const search: Search = useSearch({ strict: false });
  const weighing = typeof search[WEIGH_PARAM] === 'string';

  /**
   * The photo wizard, on the same terms: open whenever `photos` is in the URL,
   * pushed by the album's own button below and replaced away by `onClose`. One
   * screen rather than three, so the param carries no step — see the doc comment
   * on `PhotoWizard`.
   */
  const shooting = typeof search[PHOTOS_PARAM] === 'string';

  /**
   * The weighing being corrected, resolved from the live query rather than
   * trusted from the URL.
   *
   * A link to `?entry=…` can name a session somebody deleted on another phone,
   * or one belonging to a different litter entirely. Both resolve to null here,
   * and the wizard then opens as a NEW weighing — which is the honest reading of
   * "the thing you asked to edit is not there" and, importantly, is not a wizard
   * silently saving corrections into nothing.
   */
  const { data: entries } = useMeasurementEntries(tenantId, litterId, mayReadWeights);
  const entryId = search[ENTRY_PARAM];
  const editingEntry =
    typeof entryId === 'string'
      ? (entries?.find((one) => one.id === entryId) ?? null)
      : null;

  /**
   * Photographs handed to the app by the phone's share sheet, on their way into
   * the wizard's litter strip.
   *
   * The share screen sent the reader here with `shared` still on the URL — it names
   * a hand-off in IndexedDB, not anything of this litter's — and claiming it is
   * a one-shot: the files come back once and the store is emptied. See
   * offline/sharedPhotos.ts. `null` for a page nobody shared into, which is
   * every other visit.
   */
  const shareId = typeof search[SHARE_ID_PARAM] === 'string' ? search[SHARE_ID_PARAM] : null;
  const shared = useSharedPhotos(shareId);

  /** The photographing being corrected, resolved the same way and for the same reasons. */
  const { data: sessions } = usePhotoSessions(tenantId, litterId, mayReadPhotos);
  const sessionId = search[SESSION_PARAM];
  const editingSession =
    typeof sessionId === 'string'
      ? (sessions?.find((one) => one.id === sessionId) ?? null)
      : null;

  const mayEdit = role !== null && canWriteLitters(role);
  const mayWeigh = role !== null && canWriteHealth(role);
  const offspring = inLitter(all, litterId);

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs links={[{ label: t('nav.litters'), to: '/' }]} current={t('common.loading')} />
        <p className="text-ink-faint">{t('common.loading')}</p>
      </div>
    );
  }

  /**
   * A litter that is only PLANNED is not a viewer's to read — see
   * `canSeePlannedLitters`. The home screen does not list it; this is the other
   * way in, from a link or from a tab left open before the role changed.
   *
   * It ends in the same dead end as a litter somebody deleted, and that is the
   * point: "there is no such litter here" is the whole answer, where a second,
   * different message would confirm to the reader that the mating exists.
   */
  const planned =
    litter !== null &&
    litter !== undefined &&
    litter.status === 'incoming' &&
    !(role !== null && canSeePlannedLitters(role));

  // `null` is an answer and not a loading state — a link followed from a stale
  // cache to a litter somebody else deleted lands here, and it must say so
  // rather than spin. See useLiveQuery.ts.
  if (litter === null || litter === undefined || planned) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs links={[{ label: t('nav.litters'), to: '/' }]} current={t('litter.missing')} />
        <p className="text-ink-faint">{t('litter.missing')}</p>
      </div>
    );
  }

  // Full-page, not a panel under the board: weighing is a task that wants the
  // whole screen's attention and nothing to scroll past to get back to it. See
  // components/WeighWizard.tsx.
  if (weighing) {
    return (
      <div className="flex flex-col gap-5">
        <WeighWizard
          tenantId={tenantId}
          litterId={litterId}
          offspring={offspring}
          litter={litter}
          entry={editingEntry}
          recordedBy={uid}
          onClose={() =>
            void navigate({
              // Both params, always: leaving `entry` behind would reopen the
              // wizard on a correction the next time somebody taps Weigh.
              // @ts-expect-error - neither param is in any route's search schema
              search: (prev: Search) => withoutParam(ENTRY_PARAM)(withoutParam(WEIGH_PARAM)(prev)),
              replace: true,
              resetScroll: false,
            })
          }
        />
      </div>
    );
  }

  // Full-page for the same reason weighing is: adding an afternoon's
  // photographs wants the whole screen, and nothing above it to scroll past.
  if (shooting) {
    return (
      <div className="flex flex-col gap-5">
        <PhotoWizard
          tenantId={tenantId}
          litterId={litterId}
          offspring={offspring}
          litter={litter}
          sessions={sessions ?? []}
          session={editingSession}
          incoming={shared ?? undefined}
          recordedBy={uid}
          onClose={() =>
            void navigate({
              // All three, always: leaving `session` behind would reopen the
              // wizard on a correction the next time somebody taps Add photos,
              // and leaving `shared` behind would leave a spent hand-off named
              // in a URL somebody can bookmark.
              // @ts-expect-error - none of the three is in any route's search schema
              search: (prev: Search) =>
                withoutParam(SHARE_ID_PARAM)(
                  withoutParam(SESSION_PARAM)(withoutParam(PHOTOS_PARAM)(prev)),
                ),
              replace: true,
              resetScroll: false,
            })
          }
        />
      </div>
    );
  }

  const dam = animals?.find((animal) => animal.id === litter.damId) ?? null;
  const sire = animals?.find((animal) => animal.id === litter.sireId) ?? null;

  /**
   * ADDING A PUPPY IS AN EDIT TO THE LITTER, AND LIVES IN THE LITTER'S EDITOR.
   *
   * It used to sit beside the board, secondary to "Weigh puppies" — which put
   * the two things on the same footing and they are not. A litter's roster is
   * written once, in the day or two around whelping; the weighing happens twice
   * a day for eight weeks afterwards. A permanent second button for the rare
   * job is a button the reader has to read past every single time, and on a
   * phone it was the pair that wrapped the header onto two rows.
   *
   * So it moved behind the pencil, with everything else that changes what this
   * litter *is*, and the board is left with the one thing it is opened to do.
   * See the empty state below, which is now the only other way in.
   */
  const addButton = mayEdit && !adding && (
    <Button
      variant="secondary"
      className="min-h-tap gap-1.5 self-start px-4 text-sm"
      onClick={() => setAdding(true)}
    >
      <Plus className="size-4" aria-hidden />
      {t('offspring.add')}
    </Button>
  );

  // The one control on the board, and the thing this page is opened to do.
  const weighButton = mayWeigh && !adding && offspring.length > 0 && (
    <Button
      className="min-h-tap gap-1.5 px-4 text-sm"
      onClick={() => {
        setAdding(false);
        void navigate({
          // `entry` is dropped, not merely left unset: this button starts a new
          // weighing, and a stale id from a correction closed by the back button
          // would turn it into another correction of that session.
          // @ts-expect-error - neither param is in any route's search schema
          search: (prev: Search) => ({ ...withoutParam(ENTRY_PARAM)(prev), [WEIGH_PARAM]: 'select' }),
          resetScroll: false,
        });
      }}
    >
      <Scale className="size-4" aria-hidden />
      {t('offspring.weighPuppies')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <Breadcrumbs links={[{ label: t('nav.litters'), to: '/' }]} current={litter.name} />
        <div className="flex items-center gap-2">
          <h1 className="min-w-0 flex-1 text-2xl font-bold tracking-[-0.02em]">{litter.name}</h1>
          {mayEdit && editing === null && (
            // Left of the badge, deliberately: the badge is the last flex item
            // either way, so it stays flush against the right edge whether or
            // not this button is on screen. Put it on the other side of the
            // badge and hiding it while editing shifts the badge into the gap
            // it left behind.
            <Button
              variant="quiet"
              aria-label={t('common.edit')}
              className="min-h-tap w-11 shrink-0 px-0"
              onClick={() => {
                setEditing(litterDraftOf(litter, animals ?? []));
                editPanel.markOpen();
              }}
            >
              <Pencil className="size-4" aria-hidden />
            </Button>
          )}
          <LitterStatusBadge status={litter.status} />
        </div>
      </div>

      {editing !== null ? (
        <div className="flex flex-col gap-3">
          <LitterForm
            tenantId={tenantId}
            initial={editing}
            mode="edit"
            submitLabel={() => t('common.save')}
            onCancel={closeEditor}
            onSubmit={(fields) => {
              // A patch of exactly the fields this form owns — `notes` is not on
              // this screen and a whole-document write would clear it. `closedAt`
              // is derived rather than asked for, and an existing closing date
              // is kept: a litter edited while already archived did not close
              // today.
              updateLitter(tenantId, litter.id, {
                ...fields,
                closedAt:
                  fields.status === 'historical' ? (litter.closedAt ?? Date.now()) : null,
              });
              closeEditor();
            }}
          />

          {/*
            The roster, under the litter's own fields and inside the same
            editor: a new puppy is an edit to this litter, and this is where
            edits to it are made. The form replaces the button rather than
            opening under it, so there is one thing on screen at a time —
            `OffspringDraft` is a tall card and a phone has room for it or for
            the litter's form, not both plus a button between them.
          */}
          {adding ? (
            <AddOffspring
              tenantId={tenantId}
              litterId={litterId}
              siblings={offspring}
              // A puppy added to a litter that is still expected has not been
              // born, so there is no birth weight to insist on. See `isComplete`.
              needsBirthWeight={litter.status !== 'incoming'}
              onClose={() => setAdding(false)}
            />
          ) : (
            addButton
          )}
        </div>
      ) : (
        <LitterFacts
          litter={litter}
          damName={dam?.name ?? null}
          sireName={sire?.name ?? null}
          count={offspring.length}
        />
      )}

      <section className="flex flex-col gap-3">
        {/* One button beside the title, which is why this row no longer needs the
            wrapping dance it used to: "Add" and "Weigh puppies" side by side was
            the row that broke first on a narrow phone, and "Add" has moved into
            the editor — see the note on `addButton`. */}
        <div className="flex items-center gap-3">
          <h2 className="flex-1 text-lg font-bold tracking-[-0.01em]">
            {t('offspring.title')}
            {offspring.length > 0 && (
              <span className="ml-2 text-base font-normal text-ink-faint">{offspring.length}</span>
            )}
          </h2>
          {weighButton}
        </div>

        {offspring.length === 0 ? (
          // The empty board says where the puppies are added now, because the
          // button that used to be on this row is not on it any more. An
          // `incoming` litter is told to come back instead: its puppies are not
          // late, they are unborn.
          <p className="py-6 text-center text-sm text-ink-faint">
            {litter.status === 'incoming'
              ? t('offspring.emptyIncoming')
              : mayEdit
                ? t('offspring.emptyWhere')
                : t('offspring.empty')}
          </p>
        ) : (
          <OffspringGrid offspring={offspring} />
        )}
      </section>

      {/*
        Under the board and above the album, which is the order a breeder reads
        this page in: who is here, how they are doing, then the pictures. Drawn
        only when there are puppies to draw — an `incoming` litter has none, and a
        chart of nothing is worse than no chart.
      */}
      {offspring.length > 0 && mayReadWeights && (
        <GrowthSection
          tenantId={tenantId}
          litter={litter}
          offspring={offspring}
          action={
            mayEdit ? (
              <Button
                variant="secondary"
                className="min-h-tap self-start px-4 text-sm"
                onClick={() => {
                  setEditing(litterDraftOf(litter, animals ?? []));
                  editPanel.markOpen();
                }}
              >
                <Pencil className="size-4" aria-hidden />
                {t('common.edit')}
              </Button>
            ) : undefined
          }
        />
      )}

      {/*
        The audit, under the growth section it audits and above the album:
        closed, because it answers a question asked occasionally, and last of the
        three because it is the least of them. See components/growth/MeasurementLog.tsx.

        NOT FOR AN OBSERVER, and that is a stronger condition than the scope
        above. The scope decides whether somebody may read the weighings; this
        log is not the weighings, it is the record of WHO ENTERED THEM — the
        other people in the kennel, by name, with the corrections they made.
        Somebody let in to watch a puppy grow was given the numbers and not the
        staff rota, and every control on the row is one they may not press
        anyway. So it is gated on the role whose work it audits.
      */}
      {mayWeigh && (
        <MeasurementLog
          tenantId={tenantId}
          litter={litter}
          offspring={offspring}
          onEdit={(id) =>
            void navigate({
              // The same wizard the button above opens, on this session — see the
              // doc comment on `WeighWizard`. A push, like every other step of it,
              // so the back button steps out of the correction.
              // @ts-expect-error - neither param is in any route's search schema
              search: (prev: Search) => ({ ...prev, [WEIGH_PARAM]: 'select', [ENTRY_PARAM]: id }),
              resetScroll: false,
            })
          }
        />
      )}

      {/*
        What is still to be done to this litter, between the numbers and the
        pictures — and that is where a breeder reads it: how they are doing, what
        is next, then the album. It is the one section on this page about the
        future rather than about the record, which is why it is not folded away
        like the two logs around it.

        Reading it is its own scope. An observer invited to watch the weights
        alone is refused this collection by the rules, and a refused listener
        fails terminally — so the section is not mounted at all rather than
        mounted empty. See `canView` and api/tasks.ts.

        Writing is `mayWeigh` — `canWriteHealth`, the people who do the daily
        animal work. An observer who was given the list reads it with no boxes
        and no pencils, the way `PhotoTimeline` hands over an album with no Add
        button.
      */}
      {mayReadTasks && (
        <TaskSection tenantId={tenantId} litter={litter} mayWrite={mayWeigh} uid={uid} />
      )}

      {/*
        The archive, and the only place this litter's photographs live: every
        photographing it has had, newest first, as piles of prints on a line.
        Add opens the photo wizard, which writes a dated session. `sessions` is
        the query this page already runs for the correction wizard, passed down
        rather than listened to twice. See components/gallery/PhotoTimeline.tsx.
      */}
      {mayReadPhotos && (
        <PhotoTimeline
          litter={litter}
          offspring={offspring}
          sessions={sessions ?? []}
          // Absent for a reader who may not add photographs — the button is the
          // whole of what the permission gates here. See `PhotoTimeline`.
          onAdd={
            mayEdit
              ? () => {
                  setAdding(false);
                  void navigate({
                    // `session` is dropped, not merely left unset: this button starts
                    // a new photographing, and a stale id from a correction closed by
                    // the back button would turn it into another correction of that
                    // session.
                    // @ts-expect-error - neither param is in any route's search schema
                    search: (prev: Search) => ({
                      ...withoutParam(SESSION_PARAM)(prev),
                      [PHOTOS_PARAM]: 'add',
                    }),
                    resetScroll: false,
                  });
                }
              : undefined
          }
        />
      )}

      {/*
        The archive's audit, under the archive, exactly as the weighing log sits
        under the growth section: who added photographs and when. Closed, and it
        draws nothing at all for a litter nobody has photographed yet. See
        components/gallery/PhotoLog.tsx.

        Kept from an observer for the reason the weighing log is: the pictures
        are what they were shown, and who uploaded them on which evening is the
        kennel's own record. `mayEdit` rather than `mayWeigh`, because that is
        the role every control in this log needs.
      */}
      {mayEdit && (
        <PhotoLog
          tenantId={tenantId}
          litter={litter}
          onEdit={(id) =>
            void navigate({
              // The same wizard the album's button opens, on this session — see the
              // doc comment on `PhotoWizard`. A push, so the back button steps out
              // of the correction.
              // @ts-expect-error - neither param is in any route's search schema
              search: (prev: Search) => ({ ...prev, [PHOTOS_PARAM]: 'add', [SESSION_PARAM]: id }),
              resetScroll: false,
            })
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * One more puppy, in a panel inside the litter's editor.
 *
 * The same card the wizard's second step uses, so the two cannot drift — see
 * components/OffspringDraft.tsx. Saving writes and closes; nothing is awaited,
 * because the board below is fed by a listener that has the change before the
 * panel has finished unmounting.
 */
function AddOffspring({
  tenantId,
  litterId,
  siblings,
  needsBirthWeight,
  onClose,
}: {
  tenantId: string;
  litterId: string;
  siblings: readonly Offspring[];
  needsBirthWeight: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftAt(siblings.length));
  const [showErrors, setShowErrors] = useState(false);

  const save = () => {
    if (
      !isComplete(draft, needsBirthWeight) ||
      draft.sex === null ||
      draft.markerColor === null
    ) {
      setShowErrors(true);
      return;
    }

    createOffspring({
      tenantId,
      litterId,
      name: draft.name,
      sex: draft.sex,
      markerColor: draft.markerColor,
      // One past the last, so a puppy added later sits at the end of the board
      // rather than displacing one already numbered.
      sequence: siblings.reduce((max, young) => Math.max(max, young.sequence + 1), 0),
      birthWeightGrams: birthWeightOf(draft),
      photoPath: draft.photoPath,
    });

    onClose();
  };

  return (
    <div className="flex flex-col gap-3">
      <DraftCard
        tenantId={tenantId}
        draft={draft}
        index={siblings.length}
        taken={siblings.map((young) => young.markerColor)}
        needsBirthWeight={needsBirthWeight}
        showErrors={showErrors}
        onChange={(change) => setDraft({ ...draft, ...change })}
      />

      <div className="flex gap-2">
        <Button full onClick={save}>
          {t('common.save')}
        </Button>
        <Button variant="secondary" className="px-5" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
